import React from "react";
import { StyleSheet, View } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import { colors, radii, spacing, shadowStyles } from "../../../../styles/design-system";

export interface StrugglingConceptItem {
  id: string;
  title: string;
  activityCount: number;
  struggleCount: number;
}

interface TopStrugglingConceptsProps {
  concepts: StrugglingConceptItem[];
  title?: string;
}

const TopStrugglingConcepts: React.FC<TopStrugglingConceptsProps> = ({
  concepts,
  title = "Top Struggling Concepts",
}) => {
  return (
    <View style={[styles.container, shadowStyles.sm]}>
      <CustomText text={title} size="xl" weight="bold" useTranslationText={false} style={styles.title} />

      <CustomSpacing type="vertical" size="xl" />

      {concepts.length > 0 ? (
        concepts.map((concept, index) => (
          <React.Fragment key={concept.id}>
            <View style={styles.item}>
              <View style={styles.itemContent}>
                <CustomText
                  text={concept.title}
                  size="l"
                  weight="bold"
                  useTranslationText={false}
                  style={styles.itemTitle}
                />
                <CustomText
                  text={`Appears in ${concept.activityCount} activities`}
                  size="s"
                  useTranslationText={false}
                  style={styles.itemSubtitle}
                />
              </View>

              <View style={styles.badge}>
                <CustomText
                  text={`${concept.struggleCount}x`}
                  size="m"
                  weight="bold"
                  useTranslationText={false}
                  style={styles.badgeText}
                />
              </View>
            </View>

            {index < concepts.length - 1 ? <CustomSpacing type="vertical" size="l" /> : null}
          </React.Fragment>
        ))
      ) : (
        <View style={styles.emptyState}>
          <CustomText
            text="No struggling concepts yet"
            size="m"
            useTranslationText={false}
            style={styles.emptyStateText}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  title: {
    color: colors.grey[900],
  },
  item: {
    backgroundColor: colors.error[25],
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.error[100],
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  itemContent: {
    flex: 1,
    gap: spacing.xxs,
  },
  itemTitle: {
    color: colors.grey[900],
  },
  itemSubtitle: {
    color: colors.grey[600],
  },
  badge: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.error[100],
    borderRadius: radii.full,
    minWidth: 40,
    paddingHorizontal: spacing.xs,
    paddingVertical: 6,
  },
  badgeText: {
    color: colors.error[600],
  },
  emptyState: {
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderStyle: "dashed",
    padding: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateText: {
    color: colors.grey[500],
  },
});

export default TopStrugglingConcepts;
